//@ts-nocheck
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import {
  useMeQuery,
  User,
  InfluenceStatus,
  ProfileBoostFieldsFragment,
} from "../gql/generated";
import { useApolloClient } from "@apollo/client";
import { useAtom } from "jotai";
import { authAtom } from "@src/atoms";
import type { Socket } from "socket.io-client";
import { getApiBaseUrl } from "@src/utils/api";

export enum SocketCommands {
  START_CONVERSATION = "startConversation",
  SEND_MESSAGE = "sendMessage",
  NEW_MESSAGE = "newMessage",
  CHECK_CONVERSATION = "checkConversation",
  GET_CONVERSATIONS = "getConversations",
  GET_MESSAGES = "getMessages",
  BLOCK_USER = "blockUser",
  CREATE_CONVERSATION = "createConversation",
  GET_USER_PROFILE = "getUserProfile",
  JOIN_COMMUNITY_CHAT = "joinCommunityChat",
  SEND_COMMUNITY_MESSAGE = "sendCommunityMessage",
  NEW_COMMUNITY_MESSAGE = "newCommunityMessage",
}

export enum SocketListeners {
  JOIN_CONVERSATION = "joinConversation",
  MESSAGE = "message",
  ERROR = "error",
  GET_CONVERSATIONS = "getConversations",
  NEW_MESSAGE = "newMessage",
  GET_USER_PROFILE = "getUserProfile",
  NOTIFICATION = "notificationReceived",
  UNIVERSITY_GROUP_MESSAGE_NEW = "universityGroupMessage:new",
  UNIVERSITY_GROUP_MESSAGE_REPLY = "universityGroupMessage:reply",
  CALL_NOTIFICATION = "callNotificationReceived",
}

interface IUserContextType {
  user: User | undefined;
  influenceStatus: InfluenceStatus | null;
  loading: boolean;
  error: any;
  refetch: () => void;
  socket: Socket | null;
  isSocketConnected: boolean;
  headline: string;
  myBannerImage: string | null;
  refetchMyBannerImage: () => void;
  totalConnections: number | undefined;
  refetchTotalConnections: () => void;
  refetchHeadline: () => Promise<void>;
  setHeadline: React.Dispatch<React.SetStateAction<string>>;
  profileViewsLast7Days: number;
  activeProfileBoost: ProfileBoostFieldsFragment | null;
}

const UserContext = createContext<IUserContextType | undefined>(undefined);

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<IUser | undefined>(undefined);
  const [auth, setAuth] = useAtom(authAtom);
  const apolloClient = useApolloClient();
  // checkAuth() already fetched the full Me query into the Apollo cache, so read it
  // from there instead of hitting the network again. refetch() still goes to the network.
  const { data, loading, error, refetch } = useMeQuery({
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
    skip: auth.isAuthenticated !== true,
    notifyOnNetworkStatusChange: true,
  });
  const [headline, setHeadline] = useState<string>("");
  const socketRef = useRef<Socket | null>(null);
  const [isSocketConnected, setIsSocketConnected] = useState(false);

  // Headline, banner, connection count, profile views and active boost all come back
  // with the single Me query — no separate requests. Refreshing any of them refetches Me.
  useEffect(() => {
    setHeadline(data?.me?.headline ?? "");
  }, [data?.me?.headline]);

  const totalConnections = data?.myConnectionCount ?? undefined;
  const refetchMe = async () => {
    await refetch();
  };
  // Clear user data when logged out (me query is skipped until auth is true again)
  useEffect(() => {
    if (auth.isAuthenticated) return;
    // Drop the cached Me result so the next login (cache-first) never shows the previous user
    apolloClient.cache.evict({ id: "ROOT_QUERY", fieldName: "me" });
    apolloClient.cache.evict({ id: "ROOT_QUERY", fieldName: "influenceStatus" });
    apolloClient.cache.evict({ id: "ROOT_QUERY", fieldName: "myConnectionCount" });
    apolloClient.cache.evict({ id: "ROOT_QUERY", fieldName: "myProfileViewsLast7Days" });
    apolloClient.cache.evict({ id: "ROOT_QUERY", fieldName: "myActiveProfileBoost" });
    apolloClient.cache.gc();
    setUser(undefined);
    setIsSocketConnected(false);
    setHeadline("");
    socketRef.current?.disconnect();
    socketRef.current = null;
  }, [auth.isAuthenticated, apolloClient]);

  useEffect(() => {
    if (data?.me) {
      const updatedUser: IUser = {
        id: data.me.id,
        firstName: data.me.firstName,
        middleName: data.me.middleName ?? null,
        lastName: data.me.lastName ?? "",
        title: data.me.title ?? null,
        email: data.me.email,
        universityEmail: data.me.universityEmail ?? null,
        phone: data.me.phone,
        profileImageUrl: data.me.profileImageUrl ?? null,
        imageUrl: data.me.imageUrl ?? null,
        bannerImageUrl: data.me.bannerImageUrl ?? null,
        dentalLicenseNumber: data.me.dentalLicenseNumber ?? null,
        openToWork: data.me.openToWork,
        isHiring: data.me.isHiring,
        isVerified: data.me.isVerified,
        role: data.me.role,
        status: data.me.status ?? null,
        expertise: data.me.expertise ?? null,
        profileVisitsCount: data.me.profileVisitsCount,
        headline: data.me.headline ?? null,
        about: data.me.about ?? null,
        state: data.me.state ?? null,
        mentorSettings: data.me.mentorSettings ?? null,
        influenceBadge: data.me.influenceBadge ?? null,
        companyName: data.me.companyName ?? null,
        createdAt: data.me.createdAt,
        updatedAt: data.me.updatedAt,
      };

      setUser((prev) => ({
        ...updatedUser,
        title: updatedUser.title ?? prev?.title ?? null,
      }));
      setAuth({
        isAuthenticated: true,
        user: {
          ...updatedUser,
          title: updatedUser.title ?? user?.title ?? null,
        } as any,
      });
    }
  }, [data]);

  // Setup socket when authenticated (dynamic import keeps socket.io off the first-load chunk)
  useEffect(() => {
    if (!auth?.isAuthenticated || !user) return;

    let cancelled = false;

    import("socket.io-client").then(({ io }) => {
      if (cancelled) return;

      socketRef.current = io(getApiBaseUrl(), {
        transports: ["websocket"],
        auth: { userId: user.id },
      });

      socketRef.current.on("connect", () => setIsSocketConnected(true));
      socketRef.current.on("disconnect", () => setIsSocketConnected(false));
    });

    return () => {
      cancelled = true;
      socketRef.current?.disconnect();
      socketRef.current = null;
      setIsSocketConnected(false);
    };
  }, [auth?.isAuthenticated, user]);

  // Authenticated but profile not loaded yet → treat as loading (gates PrivateRoute).
  // Stop waiting once the me query settles (error or empty), so we never spin forever.
  const userLoading =
    auth.isAuthenticated === true && !user
      ? Boolean(loading || (error == null && data === undefined))
      : Boolean(loading);

  return (
    <UserContext.Provider
      value={{
        user,
        influenceStatus: auth.isAuthenticated
          ? (data?.influenceStatus ?? null)
          : null,
        loading: userLoading,
        error,
        refetch,
        socket: socketRef.current,
        isSocketConnected,
        headline,
        myBannerImage: data?.me?.bannerImageUrl ?? null,
        refetchMyBannerImage: refetchMe,
        setHeadline,
        totalConnections,
        refetchTotalConnections: refetchMe,
        refetchHeadline: refetchMe,
        profileViewsLast7Days: data?.myProfileViewsLast7Days ?? 0,
        activeProfileBoost: data?.myActiveProfileBoost ?? null,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error("useUser must be used within a UserProvider");
  return ctx;
};
